/**
 * Jackpot feature domain model (CASENG-926).
 *
 * The admin sets a baseline cash amount per Alea game; live pot increases are
 * read from the feed. The reserved collection `special_jackpot` is the rail.
 */

export const JACKPOT_COLLECTION_CODE = "special_jackpot";
export const JACKPOT_TAG_ID = "special_jackpot";
export const JACKPOT_PROVIDER = "alea";

export type JackpotGameRow = {
  provider: string;
  gameId: string;
  name: string;
  image?: string;
  /** Baseline cash amount set by the operator (> 0). */
  amount: number;
  isActive: boolean;
  /** Local-only: row was added in this session and not yet saved. */
  pending?: boolean;
};

export type JackpotConfig = {
  enabled: boolean;
  games: JackpotGameRow[];
};

export const DEFAULT_JACKPOT_CONFIG: JackpotConfig = {
  enabled: false,
  games: [],
};

export function jackpotGameKey(game: {
  provider: string;
  gameId: string;
}): string {
  return `${game.provider}::${game.gameId}`;
}

export function isJackpotConfigEqual(a: JackpotConfig, b: JackpotConfig): boolean {
  if (a.enabled !== b.enabled) return false;
  if (a.games.length !== b.games.length) return false;
  // Order-independent: refresh may return members in a different order than
  // the staged list without meaning the operator has unsaved changes.
  const left = [...a.games].sort((x, y) =>
    jackpotGameKey(x).localeCompare(jackpotGameKey(y)),
  );
  const right = [...b.games].sort((x, y) =>
    jackpotGameKey(x).localeCompare(jackpotGameKey(y)),
  );
  for (let i = 0; i < left.length; i += 1) {
    const l = left[i];
    const r = right[i];
    if (
      l.provider !== r.provider ||
      l.gameId !== r.gameId ||
      l.amount !== r.amount ||
      l.name !== r.name ||
      l.isActive !== r.isActive
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Validate a baseline amount string. Must be finite, > 0, max 2 decimal places.
 */
export function parseJackpotAmount(raw: string): number | null {
  const trimmed = raw.trim().replace(/,/g, "");
  if (trimmed === "") return null;
  if (!/^\d+(\.\d{0,2})?$/.test(trimmed)) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

export function isJackpotAmountValid(amount: number | null | undefined): boolean {
  if (typeof amount !== "number" || !Number.isFinite(amount) || amount <= 0) {
    return false;
  }
  // Max two decimal places.
  return Math.round(amount * 100) / 100 === amount;
}

export function isJackpotConfigValid(config: JackpotConfig): boolean {
  return config.games.every((g) => isJackpotAmountValid(g.amount));
}

/** Format amount for display, e.g. $10,000.00 */
export function formatJackpotAmount(
  amount: number,
  currencySymbol = "$",
): string {
  const formatted = amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${currencySymbol}${formatted}`;
}

export function extractJackpotAmountFromTags(
  tags: Array<{ tag_id?: string; meta?: { amount?: number | string } | null }> | undefined,
): number | null {
  if (!Array.isArray(tags)) return null;
  const jackpot = tags.find((t) => t.tag_id === JACKPOT_TAG_ID);
  const raw = jackpot?.meta?.amount;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string") {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
